import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
} from "recharts";
import { CSVLink } from "react-csv";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import UserService from "../services/user.service";
import EventBus from "../common/EventBus";

// =============================================================================
// Dashboard (Folgeprompt 15)
// -----------------------------------------------------------------------------
// Modernes, fuer JEDE Stufe optisch identisches Dashboard (Layout datengetrieben).
// Loest die fruehere Roh-Ansicht von /customerinfo ab. Kennzahlen kommen
// rollen-/stufengescopt aus dem Backend (GET /api/test/account/stats).
//   - Leere Zustaende werden sauber abgefangen ("nicht verfuegbar" / 0).
//   - Token/Debug-Infos nur im Dev-Modus (REACT_APP_DEBUG === "true").
//   - Export der Kennzahlen als CSV und Excel (XLSX).
// =============================================================================

const IS_DEBUG = process.env.REACT_APP_DEBUG === "true";

// Anzeige-Metadaten je Kennzahl (i18n-freundlich an einer Stelle gebuendelt).
const METRIC_DEFS = [
  { key: "zugriffe", label: "Zugriffe", type: "count" },
  { key: "bestand", label: "Bestände", type: "count" },
  { key: "eingeloest", label: "Eingelöste Produkte", type: "count" },
  { key: "verkommissioniert", label: "Verkommissioniert", type: "count" },
  { key: "transaktionen", label: "Transaktionen", type: "count" },
  { key: "verkaufsumsatz", label: "Verkaufsumsatz", type: "money" },
  { key: "einkassiert", label: "Einkassiert", type: "money" },
  { key: "pendelnd", label: "Pendelnd (offen)", type: "money" },
  { key: "gewinn", label: "Gewinn", type: "money" },
];

// Folgeprompt 16 (3.2): Kennzahlen je Produktgruppe. Nur mengen-/code-basierte
// Werte sind sauber je Gruppe ableitbar (Backend account.controller getMyStats).
const GROUP_METRIC_DEFS = [
  { key: "zugriffe", label: "Zugriffe" },
  { key: "bestand", label: "Bestände" },
  { key: "eingeloest", label: "Eingelöste Produkte" },
  { key: "verkommissioniert", label: "Verkommissioniert" },
];

const numberFmt = new Intl.NumberFormat("de-DE");
const moneyFmt = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });

function formatValue(value, type) {
  if (value === null || value === undefined) return "—";
  if (type === "money") return moneyFmt.format(Number(value) || 0);
  return numberFmt.format(Number(value) || 0);
}

const Dashboard = () => {
  const { user: currentUser } = useSelector((state) => state.auth);

  const [metrics, setMetrics] = useState(null);
  const [productGroups, setProductGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setErrorMsg(null);

    UserService.getMyAccountStats()
      .then((response) => {
        if (!active) return;
        setMetrics(response.data && response.data.metrics ? response.data.metrics : {});
        setGeneratedAt(response.data ? response.data.generatedAt : null);
        setProductGroups(
          response.data && Array.isArray(response.data.byProductGroup)
            ? response.data.byProductGroup
            : []
        );
      })
      .catch((error) => {
        if (!active) return;
        if (error.response && error.response.status === 401) {
          EventBus.dispatch("logout");
          return;
        }
        const msg =
          (error.response && error.response.data && error.response.data.message) ||
          "Die Kennzahlen konnten nicht geladen werden.";
        setErrorMsg(msg);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  // Tabellarische Form fuer Export + Diagramme (immer aus dem aktuellen State).
  const rows = useMemo(() => {
    return METRIC_DEFS.map((def) => {
      const raw = metrics ? metrics[def.key] : null;
      return {
        key: def.key,
        Kennzahl: def.label,
        type: def.type,
        value: raw === undefined ? null : raw,
        Wert: raw === null || raw === undefined ? "nicht verfügbar" : raw,
      };
    });
  }, [metrics]);

  const moneyChartData = useMemo(
    () =>
      rows
        .filter((r) => r.type === "money" && r.value !== null)
        .map((r) => ({ name: r.Kennzahl, value: Number(r.value) || 0 })),
    [rows]
  );

  const countChartData = useMemo(
    () =>
      rows
        .filter((r) => r.type === "count" && r.value !== null)
        .map((r) => ({ name: r.Kennzahl, value: Number(r.value) || 0 })),
    [rows]
  );

  const csvData = useMemo(
    () => [["Kennzahl", "Wert"], ...rows.map((r) => [r.Kennzahl, r.Wert])],
    [rows]
  );

  const hasAnyData = useMemo(
    () => rows.some((r) => r.value !== null && Number(r.value) !== 0),
    [rows]
  );

  const handleExcelExport = () => {
    const aoa = [["Kennzahl", "Wert"], ...rows.map((r) => [r.Kennzahl, r.Wert])];
    const worksheet = XLSX.utils.aoa_to_sheet(aoa);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Kennzahlen");
    const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const stamp = new Date().toISOString().slice(0, 10);
    saveAs(new Blob([buffer], { type: "application/octet-stream" }), `myn-dashboard-${stamp}.xlsx`);
  };

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  const COUNT_COLORS = ["#2F7FF0", "#1E5FC0", "#4DA9FF", "#2b7a78", "#3aafa9"];
  const MONEY_COLORS = ["#2F7FF0", "#1E5FC0", "#FBBC04", "#2b7a78"];

  return (
    <div className="myn-dash">
      <header className="myn-dash__head">
        <div>
          <span className="myn-eyebrow">Dashboard</span>
          <h1 className="myn-dash__title">Willkommen, {currentUser.username}</h1>
          {generatedAt && (
            <p className="myn-dash__sub">
              Stand: {new Date(generatedAt).toLocaleString("de-DE")}
            </p>
          )}
        </div>

        <div className="myn-dash__export" aria-label="Statistiken exportieren">
          <CSVLink
            data={csvData}
            filename={`myn-dashboard-${new Date().toISOString().slice(0, 10)}.csv`}
            className="myn-btn myn-btn--ghost"
            aria-label="Als CSV exportieren"
          >
            CSV-Export
          </CSVLink>
          <button type="button" className="myn-btn myn-btn--ghost" onClick={handleExcelExport}>
            Excel-Export
          </button>
        </div>
      </header>

      {errorMsg && (
        <div className="myn-alert myn-alert--danger" role="alert">
          {errorMsg}
        </div>
      )}

      {loading ? (
        <div className="myn-dash__loading" role="status" aria-live="polite">
          <span className="spinner-border" />
          <p>Kennzahlen werden geladen …</p>
        </div>
      ) : (
        <>
          <section className="myn-dash__cards" aria-label="Kennzahlen">
            {rows.map((r) => (
              <article key={r.key} className={`myn-kpi myn-kpi--${r.type}`}>
                <span className="myn-kpi__label">{r.Kennzahl}</span>
                <span className="myn-kpi__value">
                  {r.value === null ? (
                    <span className="myn-kpi__empty" title="Für diese Stufe nicht verfügbar">
                      nicht verfügbar
                    </span>
                  ) : (
                    formatValue(r.value, r.type)
                  )}
                </span>
              </article>
            ))}
          </section>

          {!hasAnyData ? (
            <div className="myn-dash__empty">
              <h3>Noch keine Daten</h3>
              <p>Für Ihren Bereich liegen aktuell keine auswertbaren Kennzahlen vor.</p>
            </div>
          ) : (
            <section className="myn-dash__charts">
              {countChartData.length > 0 && (
                <div className="myn-chart-card">
                  <h3 className="myn-chart-card__title">Mengen</h3>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={countChartData} margin={{ top: 8, right: 8, left: 0, bottom: 40 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
                      <XAxis dataKey="name" angle={-20} textAnchor="end" interval={0} height={60} tick={{ fontSize: 12 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                      <Tooltip formatter={(v) => numberFmt.format(v)} />
                      <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                        {countChartData.map((entry, index) => (
                          <Cell key={entry.name} fill={COUNT_COLORS[index % COUNT_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {moneyChartData.length > 0 && (
                <div className="myn-chart-card">
                  <h3 className="myn-chart-card__title">Finanzen</h3>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={moneyChartData} margin={{ top: 8, right: 8, left: 0, bottom: 40 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
                      <XAxis dataKey="name" angle={-20} textAnchor="end" interval={0} height={60} tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip formatter={(v) => moneyFmt.format(v)} />
                      <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                        {moneyChartData.map((entry, index) => (
                          <Cell key={entry.name} fill={MONEY_COLORS[index % MONEY_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </section>
          )}

          {/* Folgeprompt 16 (3.2): Kennzahlen je Produktgruppe. Jede Gruppe
              erhaelt einen eigenen Block; ohne Daten ein sauberer Leerzustand
              (z. B. Weiterleitungen erscheinen nur bei Review-Produkten). */}
          <section className="myn-dash__groups" aria-label="Kennzahlen je Produktgruppe">
            <h2 className="myn-dash__section-title">Nach Produktgruppe</h2>
            {productGroups.length === 0 ? (
              <div className="myn-dash__empty">
                <h3>Keine Produktgruppen</h3>
                <p>Es sind noch keine Produktgruppen angelegt.</p>
              </div>
            ) : (
              <div className="myn-dash__groups-grid">
                {productGroups.map((g) => {
                  const hasData = GROUP_METRIC_DEFS.some(
                    (d) => Number(g.metrics ? g.metrics[d.key] : 0) > 0
                  );
                  return (
                    <article key={g.showcase_id} className="myn-group-card">
                      <h3 className="myn-group-card__title">
                        {g.title || `Produktgruppe #${g.showcase_id}`}
                      </h3>
                      {!hasData ? (
                        <p className="myn-group-card__empty">
                          Noch keine Daten für diese Produktgruppe.
                        </p>
                      ) : (
                        <div className="myn-group-card__kpis">
                          {GROUP_METRIC_DEFS.map((d) => {
                            const raw = g.metrics ? g.metrics[d.key] : null;
                            return (
                              <div key={d.key} className="myn-kpi myn-kpi--count">
                                <span className="myn-kpi__label">{d.label}</span>
                                <span className="myn-kpi__value">
                                  {raw === null || raw === undefined
                                    ? "—"
                                    : numberFmt.format(Number(raw) || 0)}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        </>
      )}

      {IS_DEBUG && (
        <details className="myn-dash__debug">
          <summary>Debug-Informationen (nur Entwicklungsmodus)</summary>
          <p>
            <strong>Id:</strong> {currentUser.id}
          </p>
          <p>
            <strong>E-Mail:</strong> {currentUser.email}
          </p>
          <p>
            <strong>Rollen:</strong> {(currentUser.roles || []).join(", ")}
          </p>
        </details>
      )}
    </div>
  );
};

export default Dashboard;
